import { type ElementType } from 'react';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DoDisturb from '@mui/icons-material/DoDisturb';
import Edit from '@mui/icons-material/Edit';
import ExitToApp from '@mui/icons-material/ExitToApp';
import HowToReg from '@mui/icons-material/HowToReg';
import Lock from '@mui/icons-material/Lock';
import NoteAdd from '@mui/icons-material/NoteAdd';
import Paid from '@mui/icons-material/Paid';
import PersonAdd from '@mui/icons-material/PersonAdd';
import PersonOff from '@mui/icons-material/PersonOff';
import PersonRemove from '@mui/icons-material/PersonRemove';
import RequestPage from '@mui/icons-material/RequestPage';
import TaskAlt from '@mui/icons-material/TaskAlt';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format, isValid } from 'date-fns';
import { MatterActivityEventType } from '~/gql/graphql';
import { useMatterActivitiesQuery } from '~/modules/matters/useMatterActivitiesQuery';

interface Props {
  matterId: string;
}

interface EventMeta {
  icon: ElementType;
  label: string;
}

const EVENT_META: Record<MatterActivityEventType, EventMeta> = {
  [MatterActivityEventType.MatterCreated]: {
    icon: NoteAdd,
    label: 'Matter created',
  },
  [MatterActivityEventType.MatterEdited]: {
    icon: Edit,
    label: 'Matter edited',
  },
  [MatterActivityEventType.MatterClosed]: {
    icon: Lock,
    label: 'Matter closed',
  },
  [MatterActivityEventType.ExpertInvited]: {
    icon: PersonAdd,
    label: 'Expert invited',
  },
  [MatterActivityEventType.ExpertHired]: {
    icon: HowToReg,
    label: 'Expert accepted invitation',
  },
  [MatterActivityEventType.ExpertDeclined]: {
    icon: PersonOff,
    label: 'Expert declined invitation',
  },
  [MatterActivityEventType.ExpertRemoved]: {
    icon: PersonRemove,
    label: 'Expert removed from matter',
  },
  [MatterActivityEventType.ExpertLeft]: {
    icon: ExitToApp,
    label: 'Expert left matter',
  },
  [MatterActivityEventType.RetainerRequested]: {
    icon: RequestPage,
    label: 'Retainer requested',
  },
  [MatterActivityEventType.RetainerApproved]: {
    icon: Paid,
    label: 'Retainer approved & funded',
  },
  [MatterActivityEventType.RetainerDeclined]: {
    icon: DoDisturb,
    label: 'Retainer declined',
  },
  [MatterActivityEventType.RetainerClosed]: {
    icon: TaskAlt,
    label: 'Retainer closed',
  },
  [MatterActivityEventType.RetainerWorkCompleted]: {
    icon: CheckCircle,
    label: 'Retainer work completed',
  },
};

function formatTimestamp(date: string): string {
  const dateObj = new Date(date);
  if (!isValid(dateObj)) {
    return '—';
  }
  return format(dateObj, 'MMM d, h:mm a');
}

const ActivityFeed = ({ matterId }: Props) => {
  const { events, isLoading, error } = useMatterActivitiesQuery({ matterId });

  if (isLoading) {
    return (
      <EmptyStateWrapper>
        <Typography variant="body2" color="text.secondary">
          Loading activity…
        </Typography>
      </EmptyStateWrapper>
    );
  }

  if (error) {
    return (
      <EmptyStateWrapper>
        <Typography variant="body2" color="error.main">
          Unable to load activity. Please refresh and try again.
        </Typography>
      </EmptyStateWrapper>
    );
  }

  if (events.length === 0) {
    return (
      <EmptyStateWrapper>
        <Typography variant="body2" color="text.secondary">
          No activity yet for this matter.
        </Typography>
      </EmptyStateWrapper>
    );
  }

  return (
    <FeedStack divider={<FeedDivider />}>
      {events.map((event) => {
        const meta = EVENT_META[event.eventType];
        const Icon = meta.icon;
        return (
          <EntryRow key={event.id}>
            <EntryIcon aria-hidden>
              <Icon fontSize="inherit" color="inherit" />
            </EntryIcon>
            <EntryContent>
              <Typography variant="body2" color="text.primary">
                {meta.label}
              </Typography>
              <TimestampText variant="caption" color="text.secondary">
                {formatTimestamp(event.occurredAt)}
              </TimestampText>
            </EntryContent>
          </EntryRow>
        );
      })}
    </FeedStack>
  );
};

const EmptyStateWrapper = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

const FeedStack = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

const FeedDivider = styled(Divider)(({ theme }) => ({
  borderColor: theme.palette.grey[200],
}));

const EntryRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const EntryIcon = styled(Box)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.amount.main,
  flexShrink: 0,
  marginTop: theme.spacing(0.25),
  display: 'inline-flex',
  '& svg': {
    fontSize: 'inherit',
    color: 'inherit',
  },
}));

const EntryContent = styled(Box)(() => ({
  minWidth: 0,
  flex: 1,
}));

const TimestampText = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(0.25),
}));

export { ActivityFeed };
